import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Switch } from 'app/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { Separator } from 'app/components/ui/separator';
import { Textarea } from 'app/components/ui/textarea';
import { Button } from 'app/components/ui/button';
import {
  Key,
  Shield,
  Clock,
  Cookie,
  RefreshCw,
  Eye,
  EyeOff,
  AlertTriangle,
  CheckCircle,
  Zap,
  Save,
} from 'lucide-react';
import { useSettingsSlice } from '../slice';

interface JWTSettingsProps {
  form: UseFormReturn<any>;
}

// Helper function to create payload with is_public: 0 for all keys
const createJWTSettingsPayload = (formData: any) => {
  const payload: Record<string, { option_value: any; is_public: number }> = {};

  // Add is_public: 0 for all JWT settings (sensitive data)
  const settingsKeys = [
    'jwt_secret_key',
    'jwt_algorithm',
    'jwt_access_token_expiry',
    'jwt_refresh_token_expiry',
    'jwt_issuer',
    'jwt_audience',
    'jwt_blacklist_enabled',
    'jwt_blacklist_cleanup_interval',
    'jwt_rotation_enabled',
    'jwt_rotation_interval',
    'jwt_cookie_secure',
    'jwt_cookie_http_only',
    'jwt_cookie_same_site',
    'jwt_cookie_domain',
    'jwt_cookie_path',
  ];

  settingsKeys.forEach(key => {
    if (formData[key] !== undefined) {
      payload[key] = {
        option_value: formData[key],
        is_public: 0,
      };
    }
  });

  return payload;
};

export default function JWTSettings({ form }: JWTSettingsProps) {
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const { useSaveSiteSettingsMutation } = useSettingsSlice();
  const [saveSiteSettings, { isLoading: isUpdating }] =
    useSaveSiteSettingsMutation();

  const generateSecretKey = () => {
    setIsGeneratingKey(true);
    // Generate a random 64-character secret key
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    let result = '';
    for (let i = 0; i < 64; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    form.setValue('jwt_secret_key', result);
    setIsGeneratingKey(false);
  };

  // Function to handle settings update with is_public: 0 for all keys
  const handleUpdateSettings = async () => {
    try {
      setUpdateStatus('idle');
      const formData = form.getValues();
      const payload = createJWTSettingsPayload(formData);

      console.log('JWT settings payload with is_public: 0:', payload);

      // Use the Redux mutation to save settings
      const result = await saveSiteSettings(payload).unwrap();
      console.log('JWT settings updated successfully:', result);

      setUpdateStatus('success');
      return result;
    } catch (error) {
      console.error('Error updating JWT settings:', error);
      setUpdateStatus('error');
      throw error;
    }
  };

  return (
    <>
      {/* JWT Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            JWT Configuration
          </CardTitle>
          <CardDescription>
            Configure JSON Web Token settings for authentication and security.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="jwt_secret_key">Secret Key</Label>
            <div className="relative">
              <Input
                id="jwt_secret_key"
                type={showSecretKey ? 'text' : 'password'}
                {...form.register('jwt_secret_key')}
                placeholder="Enter your JWT secret key"
                className="pr-20"
                value={form.watch('jwt_secret_key') || ''}
              />
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={generateSecretKey}
                  disabled={isGeneratingKey}
                >
                  {isGeneratingKey ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : (
                    <Zap className="w-3 h-3" />
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSecretKey(!showSecretKey)}
                >
                  {showSecretKey ? (
                    <EyeOff className="w-3 h-3" />
                  ) : (
                    <Eye className="w-3 h-3" />
                  )}
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              This is used to sign and verify JWT tokens. Keep it secure and
              change it in production.
            </p>
          </div>

          {/* Critical Warning about Secret Key Changes */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
              <div>
                <p className="text-sm text-red-800 font-medium mb-2">
                  ⚠️ CRITICAL: Changing JWT Secret Key Will Log Out All Users
                </p>
                <div className="text-sm text-red-700 space-y-2">
                  <p>
                    <strong>
                      What happens when you change the secret key:
                    </strong>
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>All existing JWT tokens become immediately invalid</li>
                    <li>All logged-in users will be forced to log out</li>
                    <li>Users must log in again with their credentials</li>
                    <li>Any active sessions will be terminated</li>
                    <li>
                      API calls with old tokens will return 401 Unauthorized
                    </li>
                  </ul>
                  <p className="mt-2">
                    <strong>When to change the secret key:</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Security breach or suspected compromise</li>
                    <li>Regular security rotation (quarterly/yearly)</li>
                    <li>Initial setup in production environment</li>
                    <li>After developer access changes</li>
                  </ul>
                  <p className="mt-2">
                    <strong>Best practice:</strong> Plan secret key changes
                    during low-traffic periods and notify users in advance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="jwt_algorithm">Algorithm</Label>
              <Select
                value={form.watch('jwt_algorithm')}
                onValueChange={value => form.setValue('jwt_algorithm', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select algorithm" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HS256">HS256 (HMAC SHA256)</SelectItem>
                  <SelectItem value="HS384">HS384 (HMAC SHA384)</SelectItem>
                  <SelectItem value="HS512">HS512 (HMAC SHA512)</SelectItem>
                  <SelectItem value="RS256">RS256 (RSA SHA256)</SelectItem>
                  <SelectItem value="RS384">RS384 (RSA SHA384)</SelectItem>
                  <SelectItem value="RS512">RS512 (RSA SHA512)</SelectItem>
                  <SelectItem value="ES256">ES256 (ECDSA SHA256)</SelectItem>
                  <SelectItem value="ES384">ES384 (ECDSA SHA384)</SelectItem>
                  <SelectItem value="ES512">ES512 (ECDSA SHA512)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Choose the signing algorithm for JWT tokens.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="jwt_issuer">Issuer (iss)</Label>
              <Input
                id="jwt_issuer"
                {...form.register('jwt_issuer')}
                placeholder="Enter JWT issuer"
              />
              <p className="text-xs text-muted-foreground">
                The issuer claim identifies who issued the JWT.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="jwt_audience">Audience (aud)</Label>
            <Input
              id="jwt_audience"
              {...form.register('jwt_audience')}
              placeholder="Enter JWT audience"
            />
            <p className="text-xs text-muted-foreground">
              The audience claim identifies the intended recipients of the JWT.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Token Expiry Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Token Expiry Settings
          </CardTitle>
          <CardDescription>
            Configure how long JWT tokens remain valid.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="jwt_access_token_expiry">
                Access Token Expiry
              </Label>
              <Input
                id="jwt_access_token_expiry"
                {...form.register('jwt_access_token_expiry')}
                placeholder="15m"
              />
              <p className="text-xs text-muted-foreground">
                How long access tokens are valid (e.g., 15m, 1h, 1d)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="jwt_refresh_token_expiry">
                Refresh Token Expiry
              </Label>
              <Input
                id="jwt_refresh_token_expiry"
                {...form.register('jwt_refresh_token_expiry')}
                placeholder="7d"
              />
              <p className="text-xs text-muted-foreground">
                How long refresh tokens are valid (e.g., 7d, 30d)
              </p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm text-blue-800 font-medium">
                  Token Security Best Practices
                </p>
                <ul className="text-sm text-blue-700 mt-2 space-y-1">
                  <li>• Keep access tokens short-lived (15-60 minutes)</li>
                  <li>• Use refresh tokens for longer sessions (7-30 days)</li>
                  <li>• Implement token rotation for enhanced security</li>
                  <li>• Use HTTPS in production to protect tokens</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Features */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Security Features
          </CardTitle>
          <CardDescription>
            Configure additional security features for JWT tokens.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="jwt_blacklist_enabled">Token Blacklist</Label>
                <p className="text-sm text-gray-500">
                  Enable blacklisting of revoked tokens
                </p>
              </div>
              <Switch
                id="jwt_blacklist_enabled"
                checked={form.watch('jwt_blacklist_enabled')}
                onCheckedChange={value =>
                  form.setValue('jwt_blacklist_enabled', value)
                }
              />
            </div>

            {form.watch('jwt_blacklist_enabled') && (
              <div className="space-y-2">
                <Label htmlFor="jwt_blacklist_cleanup_interval">
                  Blacklist Cleanup Interval
                </Label>
                <Input
                  id="jwt_blacklist_cleanup_interval"
                  {...form.register('jwt_blacklist_cleanup_interval')}
                  placeholder="24h"
                />
                <p className="text-xs text-muted-foreground">
                  How often to clean up expired blacklisted tokens
                </p>
              </div>
            )}

            <Separator />

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="jwt_rotation_enabled">Token Rotation</Label>
                <p className="text-sm text-gray-500">
                  Automatically rotate refresh tokens
                </p>
              </div>
              <Switch
                id="jwt_rotation_enabled"
                checked={form.watch('jwt_rotation_enabled')}
                onCheckedChange={value =>
                  form.setValue('jwt_rotation_enabled', value)
                }
              />
            </div>

            {form.watch('jwt_rotation_enabled') && (
              <div className="space-y-2">
                <Label htmlFor="jwt_rotation_interval">Rotation Interval</Label>
                <Input
                  id="jwt_rotation_interval"
                  {...form.register('jwt_rotation_interval')}
                  placeholder="30d"
                />
                <p className="text-xs text-muted-foreground">
                  How often to rotate refresh tokens
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cookie Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Cookie className="w-5 h-5" />
            Cookie Settings
          </CardTitle>
          <CardDescription>
            Configure how JWT tokens are stored in cookies.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="jwt_cookie_secure">Secure Cookies</Label>
                <p className="text-sm text-gray-500">
                  Only send cookies over HTTPS
                </p>
              </div>
              <Switch
                id="jwt_cookie_secure"
                checked={form.watch('jwt_cookie_secure')}
                onCheckedChange={value =>
                  form.setValue('jwt_cookie_secure', value)
                }
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="jwt_cookie_http_only">HTTP Only</Label>
                <p className="text-sm text-gray-500">
                  Prevent JavaScript access to cookies
                </p>
              </div>
              <Switch
                id="jwt_cookie_http_only"
                checked={form.watch('jwt_cookie_http_only')}
                onCheckedChange={value =>
                  form.setValue('jwt_cookie_http_only', value)
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="jwt_cookie_same_site">Same Site Policy</Label>
              <Select
                value={form.watch('jwt_cookie_same_site')}
                onValueChange={value =>
                  form.setValue('jwt_cookie_same_site', value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select same site policy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="strict">Strict</SelectItem>
                  <SelectItem value="lax">Lax</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Controls when cookies are sent in cross-site requests
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="jwt_cookie_domain">Cookie Domain</Label>
              <Input
                id="jwt_cookie_domain"
                {...form.register('jwt_cookie_domain')}
                placeholder="Leave empty for current domain"
                value={form.watch('jwt_cookie_domain') || ''}
              />
              <p className="text-xs text-muted-foreground">
                Domain for which the cookie is valid
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="jwt_cookie_path">Cookie Path</Label>
            <Input
              id="jwt_cookie_path"
              {...form.register('jwt_cookie_path')}
              placeholder="/"
              value={form.watch('jwt_cookie_path') || ''}
            />
            <p className="text-xs text-muted-foreground">
              Path for which the cookie is valid
            </p>
          </div>

          {/* Cookie Settings Explanation */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Cookie className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium mb-3">
                  🍪 Cookie Settings Purpose & Security
                </p>
                <div className="text-sm text-blue-700 space-y-4">
                  <div>
                    <p className="font-medium">🔒 Secure Flag:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                      <li>
                        <strong>ON:</strong> Cookies only sent over HTTPS
                        (production)
                      </li>
                      <li>
                        <strong>OFF:</strong> Cookies sent over HTTP
                        (development)
                      </li>
                      <li>
                        <strong>Purpose:</strong> Prevents man-in-the-middle
                        attacks
                      </li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-medium">🛡️ HTTP Only Flag:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                      <li>
                        <strong>ON:</strong> JavaScript cannot access the cookie
                      </li>
                      <li>
                        <strong>OFF:</strong> JavaScript can read/write the
                        cookie
                      </li>
                      <li>
                        <strong>Purpose:</strong> Prevents XSS attacks from
                        stealing tokens
                      </li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-medium">🌐 Same Site Policy:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                      <li>
                        <strong>Strict:</strong> Only same-site requests (most
                        secure)
                      </li>
                      <li>
                        <strong>Lax:</strong> Same-site + top-level navigation
                        (balanced)
                      </li>
                      <li>
                        <strong>None:</strong> All cross-site requests (least
                        secure)
                      </li>
                      <li>
                        <strong>Purpose:</strong> Prevents CSRF attacks
                      </li>
                    </ul>
                  </div>

                  <div>
                    <p className="font-medium">🏠 Domain & Path:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2 mt-1">
                      <li>
                        <strong>Domain:</strong> Which domains can access the
                        cookie
                      </li>
                      <li>
                        <strong>Path:</strong> Which URL paths can access the
                        cookie
                      </li>
                      <li>
                        <strong>Purpose:</strong> Controls cookie scope and
                        access
                      </li>
                    </ul>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                    <p className="font-medium text-yellow-800">
                      💡 Recommended Settings:
                    </p>
                    <ul className="text-yellow-700 mt-1 space-y-1">
                      <li>
                        • <strong>Production:</strong> Secure=ON, HttpOnly=ON,
                        SameSite=Strict
                      </li>
                      <li>
                        • <strong>Development:</strong> Secure=OFF, HttpOnly=ON,
                        SameSite=Lax
                      </li>
                      <li>
                        • <strong>Cross-domain:</strong> SameSite=None (requires
                        Secure=ON)
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Notice */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            JWT Security Recommendations
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-green-800 font-medium">
                  Best Security Practices
                </p>
                <ul className="text-sm text-green-700 mt-2 space-y-1">
                  <li>
                    • Use a strong, unique secret key (at least 32 characters)
                  </li>
                  <li>
                    • Keep access tokens short-lived and refresh tokens longer
                  </li>
                  <li>• Enable token blacklisting for logout functionality</li>
                  <li>• Use HTTPS in production environments</li>
                  <li>• Implement token rotation for enhanced security</li>
                  <li>
                    • Set secure cookie flags (Secure, HttpOnly, SameSite)
                  </li>
                  <li>• Regularly rotate your JWT secret key</li>
                  <li>• Monitor for token abuse and implement rate limiting</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Secret Key Change Guidelines */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <div className="flex items-start gap-3">
              <Key className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium mb-2">
                  Secret Key Change Guidelines
                </p>
                <div className="text-sm text-blue-700 space-y-2">
                  <p>
                    <strong>Before changing the secret key:</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Notify all users about planned maintenance</li>
                    <li>Choose a low-traffic time period</li>
                    <li>Have a rollback plan ready</li>
                    <li>Test the change in staging environment first</li>
                    <li>Ensure all systems are updated with the new key</li>
                  </ul>
                  <p className="mt-2">
                    <strong>After changing the secret key:</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Monitor authentication logs for issues</li>
                    <li>Check that all services are using the new key</li>
                    <li>
                      Verify that new tokens are being generated correctly
                    </li>
                    <li>Update any external integrations that use JWT</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fixed Save Button - Bottom Right */}
      <div className="fixed bottom-14 right-6 z-50">
        <div className="flex flex-col items-end gap-3">
          {/* Status Feedback */}
          {updateStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  JWT settings saved successfully!
                </span>
              </div>
            </div>
          )}

          {updateStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Failed to save JWT settings. Please try again.
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleUpdateSettings}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
            variant="default"
            disabled={isUpdating}
            size="lg"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
